import UserAvatar from '@aurora/shared-client/components/users/UserAvatar/UserAvatar';
import UserLogin from '@aurora/shared-client/components/users/UserLogin/UserLogin';
import UserRank from '@aurora/shared-client/components/users/UserRank/UserRank';
import { SearchSnippetKey } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { getSearchSnippet } from '../../../../helpers/search/SearchSnippetHelper';
import type { ItemType, UserViewVariant } from '../../../../types/enums';
import { UserKudosType, UserViewSize } from '../../../../types/enums';
import type { UserViewFragment } from '../../../../types/graphql-types';
import type { ItemViewVariantFC } from '../../../entities/types';
import useTranslation from '../../../useTranslation';
import UserBiography from '../../UserBiography/UserBiography';
import UserKudos from '../../UserKudos/UserKudos';
import UserLink from '../../UserLink/UserLink';
import UserMembershipDateForNode from '../../UserMembershipDateForNode/UserMembershipDateForNode';
import UserMessagesCount from '../../UserMessagesCount/UserMessagesCount';
import UserRoleForNode from '../../UserRoleForNode/UserRoleForNode';
import UserSolutionCount from '../../UserSolutionCount/UserSolutionCount';
import UserTopicsCount from '../../UserTopicsCount/UserTopicsCount';
import localStyles from './UserViewInline.module.pcss';

/**
 * User View - Inline
 *
 * @constructor
 *
 * @author Adam Ayres, Willi Hyde, Amit Agrawal
 */

const UserViewInline: ItemViewVariantFC<
  UserViewFragment,
  ItemType.USER | ItemType.USER_SEARCH,
  UserViewVariant.INLINE
> = ({
  entity: user,
  className,
  useAvatar,
  avatarSize,
  useJoinDate,
  useLogin,
  useRank,
  useRole,
  useMessagesCount,
  useKudosCount,
  useTopicsCount,
  useSolutionCount,
  useClamp,
  showOwnerOnly,
  viewSize,
  useLink,
  useEntireItemAsLink,
  useTopicAndSolutionCountInline,
  useHoverCard,
  searchSnippet,
  useBio,
  contentClassName,
  nameClassName,
  rankClassName,
  biographyClassName,
  userLinkClassName
}) => {
  const cx = useClassNameMapper(localStyles);
  const { loading: textLoading } = useTranslation(EndUserComponent.USER_VIEW_INLINE);
  const loginSearchSnippet = getSearchSnippet(searchSnippet, SearchSnippetKey.Login);

  const userViewClasses = cx(
    className,
    'lia-user',
    {
      'lia-user-sm': viewSize === UserViewSize.SM
    },
    {
      'text-decoration-none': useEntireItemAsLink
    }
  );

  if (textLoading) {
    return null;
  }

  /**
   * Renders the topic and solution count inline
   */
  function renderTopicAndSolutionCount(): React.ReactElement {
    const { solutionsCount, topicsCount } = user;

    return (
      <span className={cx('d-flex align-items-center')}>
        {topicsCount > 0 && (
          <UserTopicsCount
            user={user}
            as="span"
            className={cx('lia-user-data lia-user-rank lia-g-divider')}
          />
        )}
        {solutionsCount > 0 && (
          <UserSolutionCount
            user={user}
            as="span"
            className={cx('lia-user-data lia-user-rank lia-g-divider')}
          />
        )}
      </span>
    );
  }

  /**
   * By avoiding using ConditionalWrapper here, we save a noticeable amount of render time
   * especially when many UserViewInline are present -- several hundred ms for a typical CommunityHome quilt
   */
  const contents = (
    <>
      {useAvatar && (
        <>
          {useLink && !useEntireItemAsLink ? (
            <UserLink
              user={user}
              className={cx('lia-user-avatar lia-user-link', userLinkClassName)}
              useHoverCard={useHoverCard}
            >
              <UserAvatar
                className={cx({
                  'lia-user-avatar': !useLink || !(useLink && !useEntireItemAsLink)
                })}
                user={user}
                size={avatarSize}
              />
            </UserLink>
          ) : (
            <UserAvatar
              className={cx({ 'lia-user-avatar': !useLink || !(useLink && !useEntireItemAsLink) })}
              user={user}
              size={avatarSize}
            />
          )}
        </>
      )}

      {(useLogin ||
        useRank ||
        useRole ||
        useJoinDate ||
        useMessagesCount ||
        useSolutionCount ||
        useTopicsCount ||
        useTopicAndSolutionCountInline) && (
        <span className={cx('lia-user-content', contentClassName)}>
          {useLogin && (
            <>
              {useLink && !useEntireItemAsLink ? (
                <UserLink
                  user={user}
                  className={cx('lia-user-data lia-user-link', userLinkClassName)}
                  useHoverCard={useHoverCard}
                >
                  <UserLogin
                    user={user}
                    searchSnippet={loginSearchSnippet}
                    className={cx('lia-user-name', nameClassName)}
                    useClamp={useClamp}
                  />
                </UserLink>
              ) : (
                <UserLogin
                  user={user}
                  searchSnippet={loginSearchSnippet}
                  className={cx('lia-user-name', nameClassName)}
                  useClamp={useClamp}
                />
              )}
            </>
          )}
          {useRank && (
            <UserRank user={user} className={cx('lia-user-data lia-user-rank', rankClassName)} />
          )}
          {useBio && user?.uid !== -1 && !user?.deleted && (
            <UserBiography className={cx(biographyClassName)} user={user} clampLines={5} />
          )}
          {useRole && (
            <UserRoleForNode
              user={user}
              showOwnerOnly={showOwnerOnly}
              className={cx('lia-user-data')}
            />
          )}
          {useJoinDate && (
            <UserMembershipDateForNode
              user={user}
              textKey="withPrefix"
              className={cx('lia-user-data')}
            />
          )}
          {useMessagesCount && (
            <UserMessagesCount user={user} className={cx('lia-user-data lia-user-rank')} />
          )}
          {!useTopicAndSolutionCountInline && useSolutionCount && (
            <UserSolutionCount
              user={user}
              as="span"
              className={cx('lia-user-data lia-user-rank')}
            />
          )}
          {!useTopicAndSolutionCountInline && useTopicsCount && (
            <UserTopicsCount user={user} as="span" className={cx('lia-user-data lia-user-rank')} />
          )}
          {useTopicAndSolutionCountInline && renderTopicAndSolutionCount()}
        </span>
      )}
      {useKudosCount && (
        <span className={cx('lia-user-kudos-wrap')}>
          <UserKudos
            user={user}
            userKudosType={UserKudosType.RECEIVED}
            useIcon
            useText={false}
            className={cx('lia-user-kudos')}
            as="span"
          />
        </span>
      )}
    </>
  );

  return useEntireItemAsLink ? (
    <UserLink user={user} useHoverCard={useHoverCard} className={userViewClasses}>
      {contents}
    </UserLink>
  ) : (
    <span className={userViewClasses}>{contents}</span>
  );
};

export default UserViewInline;
